import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, LogOut, Save, AlertTriangle, RefreshCw, Settings, 
  Coins, Gift, Clock, Percent, Euro, Sparkles, Shield, Database,
  Palette, History, ChevronRight, Wrench, Bell, Send, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import SecuritySettings from '@/components/SecuritySettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AdminSettings({ user, onLogout }) {
  const { isDark } = useTheme();
  
  // Theme classes
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900',
    modal: isDark ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10' : 'bg-white border-gray-200 shadow-2xl',
    sidebarItem: isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
    glow1: isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5',
    glow2: isDark ? 'bg-[#5D1822]/20' : 'bg-[#5D1822]/5',
  };
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResetPointsModal, setShowResetPointsModal] = useState(false);
  const [showFullResetModal, setShowFullResetModal] = useState(false);
  const [statsPassword, setStatsPassword] = useState('');
  const [pointsPassword, setPointsPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resettingStats, setResettingStats] = useState(false);
  const [resettingPoints, setResettingPoints] = useState(false);
  const [resettingSite, setResettingSite] = useState(false);
  const [activeSection, setActiveSection] = useState('points');
  const [customers, setCustomers] = useState([]);
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [announcement, setAnnouncement] = useState({
    title: 'Aggiornamento Fidelity Card',
    message: 'Gentili clienti, desideriamo precisare che il premio previsto dalla nostra Fidelity Card è di 5€ di sconto al raggiungimento di 10 punti. In alcune comunicazioni precedenti è stato indicato per errore un importo diverso. Ci scusiamo per l’inconveniente. La regola corretta è: 10 punti accumulati = 5€ di sconto sul prossimo acquisto. Grazie per la comprensione e per aver scelto Macelleria Borgese!',
    audience: 'all',
    user_id: '',
    send_email: true,
  });

  useEffect(() => {
    fetchSettings();
    fetchAnnouncementData();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncementData = async () => {
    try {
      const [customersResponse, historyResponse] = await Promise.all([
        axios.get(`${API}/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/admin/customer-announcements`, getAuthHeaders()),
      ]);
      setCustomers(customersResponse.data || []);
      setAnnouncementHistory(historyResponse.data || []);
    } catch (error) {
      console.error('Errore caricamento avvisi clienti:', error);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcement.title.trim() || !announcement.message.trim()) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }
    if (announcement.audience === 'single' && !announcement.user_id) {
      toast.error('Seleziona un cliente');
      return;
    }

    const targetLabel = announcement.audience === 'all'
      ? 'tutti i clienti'
      : customers.find(customer => customer.id === announcement.user_id)?.full_name || 'il cliente selezionato';
    if (!window.confirm(`Inviare questa comunicazione a ${targetLabel}?`)) return;

    setSendingAnnouncement(true);
    try {
      const response = await axios.post(
        `${API}/admin/customer-announcements`,
        announcement,
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'Comunicazione inviata');
      await fetchAnnouncementData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l’invio della comunicazione');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuthHeaders());
      toast.success('Impostazioni salvate con successo!');
    } catch (error) {
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  const handleResetStats = async () => {
    if (!statsPassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    setResettingStats(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-stats`,
        { password: statsPassword },
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'Statistiche azzerate. Punti invariati.');
      setStatsPassword('');
      setShowResetModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'azzeramento');
    } finally {
      setResettingStats(false);
    }
  };

  const handleResetMonthlyPoints = async () => {
    if (!pointsPassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    setResettingPoints(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-monthly-points`,
        { password: pointsPassword },
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'Punti di tutti i clienti azzerati!');
      setPointsPassword('');
      setShowResetPointsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'azzeramento punti');
    } finally {
      setResettingPoints(false);
    }
  };


  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFullReset = async () => {
    if (!resetPassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    setResettingSite(true);
    try {
      // Prima del reset scarica obbligatoriamente una copia completa sul Mac.
      // Se il download fallisce, il reset non parte.
      const backupResponse = await axios.post(
        `${API}/admin/backup/export`,
        {},
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadBlob(backupResponse.data, `macelleria_borgese_prima_reset_${timestamp}.json`);

      const response = await axios.post(
        `${API}/admin/reset-site`,
        { password: resetPassword },
        getAuthHeaders()
      );
      const backupName = response.data?.safety_backup;
      toast.success(
        backupName
          ? `Reset completato. Backup scaricato sul Mac e copia server creata: ${backupName}`
          : 'Reset completato. Il backup completo è stato scaricato sul Mac.'
      );
      setResetPassword('');
      setShowFullResetModal(false);
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset annullato: impossibile creare o scaricare il backup completo');
    } finally {
      setResettingSite(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className={`${t.text} text-xl flex items-center gap-3`}>
          <div className="w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
          Caricamento...
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'maintenance', label: 'Manutenzione', icon: Wrench },
    { id: 'points', label: 'Sistema Punti', icon: Coins },
    { id: 'discount', label: 'Sconti', icon: Gift },
    { id: 'expiry', label: 'Scadenza', icon: Clock },
    { id: 'communications', label: 'Avvisi clienti', icon: Bell },
    { id: 'security', label: 'Sicurezza', icon: Shield },
    { id: 'tools', label: 'Strumenti', icon: Settings },
    { id: 'danger', label: 'Zona Pericolosa', icon: AlertTriangle },
  ];

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-20 w-72 h-72 ${t.glow1} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-20 right-20 w-96 h-96 ${t.glow2} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/admin" className={`flex items-center gap-2 ${t.navLink} transition-colors`}>
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center shadow-lg shadow-[#5D1822]/20">
                <Settings className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Impostazioni</h1>
                <p className="text-sm text-[#C5A059]">Configura il sistema fidelity</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Salvataggio...' : 'Salva Tutto'}
            </button>
            <ThemeToggle />
            <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary} hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} transition-colors`}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-8 space-y-2">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white shadow-lg shadow-[#C5A059]/30'
                      : t.sidebarItem
                  }`}
                >
                  <section.icon size={20} />
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-6">
            {/* Points System Section */}
            {activeSection === 'maintenance' && (
              <div className={`rounded-2xl ${t.card} backdrop-blur-xl border p-8`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                    <Wrench className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold ${t.text}`}>Modalità manutenzione</h2>
                    <p className={t.textSecondary}>Blocca il sito ai clienti lasciando disponibile l’accesso amministratore.</p>
                  </div>
                </div>

                <label className="flex items-center justify-between gap-4 mb-6 p-4 rounded-xl border border-orange-500/30 bg-orange-500/10">
                  <div>
                    <div className={`font-bold ${t.text}`}>Attiva manutenzione</div>
                    <div className={`text-sm ${t.textSecondary}`}>Gli ordini saranno bloccati e i clienti vedranno l’avviso.</div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-6 h-6"
                    checked={settings?.maintenance_enabled || false}
                    onChange={(e) => handleChange('maintenance_enabled', e.target.checked)}
                  />
                </label>

                <div className="space-y-5">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Titolo</label>
                    <input className={`w-full px-4 py-3 rounded-xl border ${t.input}`} value={settings?.maintenance_title || ''} onChange={(e) => handleChange('maintenance_title', e.target.value)} />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Messaggio per i clienti</label>
                    <textarea rows="4" className={`w-full px-4 py-3 rounded-xl border ${t.input}`} value={settings?.maintenance_message || ''} onChange={(e) => handleChange('maintenance_message', e.target.value)} />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Ritorno previsto (facoltativo)</label>
                    <input placeholder="Esempio: oggi alle 18:00" className={`w-full px-4 py-3 rounded-xl border ${t.input}`} value={settings?.maintenance_return_time || ''} onChange={(e) => handleChange('maintenance_return_time', e.target.value)} />
                  </div>
                  <button onClick={handleSave} disabled={saving} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-[#5D1822] text-white font-bold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : 'Salva manutenzione'}
                  </button>
                </div>
              </div>
            )}

            {activeSection === 'points' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center">
                      <Coins className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Sistema Punti</h2>
                      <p className={t.textSecondary}>Configura come i clienti guadagnano punti</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Euro per Punto */}
                    <div className={`rounded-xl ${t.card} border p-6`}>
                      <div className="flex items-center gap-3 mb-4">
                        <Euro className="w-5 h-5 text-[#C5A059]" />
                        <label className={`${t.text} font-medium`}>Euro per Punto</label>
                      </div>
                      <input
                        type="number"
                        value={settings?.euros_per_point || 10}
                        onChange={(e) => handleChange('euros_per_point', parseFloat(e.target.value) || 5)}
                        className={`w-full px-4 py-3 ${t.input} border rounded-xl text-2xl font-bold text-center focus:outline-none focus:border-[#C5A059]/50 focus:ring-2 focus:ring-[#C5A059]/20`}
                        min="1"
                        step="0.5"
                      />
                      <p className={`${t.textMuted} text-sm mt-3 text-center`}>
                        Ogni €{settings?.euros_per_point || 10} spesi = 1 punto
                      </p>
                    </div>

                    {/* Punti per Sconto */}
                    <div className={`rounded-xl ${t.card} border p-6`}>
                      <div className="flex items-center gap-3 mb-4">
                        <Gift className="w-5 h-5 text-[#4A5D23]" />
                        <label className={`${t.text} font-medium`}>Punti per Sconto</label>
                      </div>
                      <input
                        type="number"
                        value={settings?.points_for_discount || 10}
                        onChange={(e) => handleChange('points_for_discount', parseInt(e.target.value) || 10)}
                        className={`w-full px-4 py-3 ${t.input} border rounded-xl text-2xl font-bold text-center focus:outline-none focus:border-[#4A5D23]/50 focus:ring-2 focus:ring-[#4A5D23]/20`}
                        min="1"
                      />
                      <p className={`${t.textMuted} text-sm mt-3 text-center`}>
                        {settings?.points_for_discount || 10} punti = €{settings?.discount_value || 5} di sconto
                      </p>
                    </div>
                  </div>

                  {/* Preview Box */}
                  <div className="mt-8 rounded-xl bg-gradient-to-r from-[#5D1822]/30 to-[#C5A059]/20 border border-[#C5A059]/30 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="w-5 h-5 text-[#C5A059]" />
                      <span className="text-[#C5A059] font-semibold">Anteprima Sistema</span>
                    </div>
                    <div className={`${t.text} text-lg`}>
                      <p className="mb-2">
                        👉 Il cliente spende <span className="text-[#C5A059] font-bold">€{(settings?.euros_per_point || 10) * (settings?.points_for_discount || 10)}</span>
                      </p>
                      <p className="mb-2">
                        ✨ Guadagna <span className="text-[#7CB342] font-bold">{settings?.points_for_discount || 10} punti</span>
                      </p>
                      <p>
                        🎁 Riceve <span className="text-[#C5A059] font-bold">€{settings?.discount_value || 5} di sconto</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Discount Section */}
            {activeSection === 'discount' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4A5D23] to-[#2E3A16] flex items-center justify-center">
                      <Gift className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Configurazione Sconti</h2>
                      <p className={t.textSecondary}>Imposta il valore dello sconto</p>
                    </div>
                  </div>

                  <div className={`rounded-xl ${t.card} border p-6 max-w-md`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Euro className="w-5 h-5 text-[#4A5D23]" />
                      <label className={`${t.text} font-medium`}>Valore Sconto (€)</label>
                    </div>
                    <input
                      type="number"
                      value={settings?.discount_value || 5}
                      onChange={(e) => handleChange('discount_value', parseFloat(e.target.value) || 5)}
                      className={`w-full px-4 py-3 ${t.input} border rounded-xl text-2xl font-bold text-center focus:outline-none focus:border-[#4A5D23]/50`}
                      min="1"
                    />
                    <p className={`${t.textMuted} text-sm mt-3 text-center`}>
                      Sconto di €{settings?.discount_value || 5} quando raggiungono {settings?.points_for_discount || 10} punti
                    </p>
                  </div>

                  {/* Info */}
                  <div className="mt-6 p-4 rounded-xl bg-[#4A5D23]/20 border border-[#4A5D23]/30">
                    <p className="text-[#7CB342] text-sm">
                      💡 <strong>Suggerimento:</strong> Uno sconto del 10% sulla spesa media è un buon incentivo per far tornare i clienti.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Expiry Section */}
            {activeSection === 'expiry' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF9800] to-[#F57C00] flex items-center justify-center">
                      <Clock className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Scadenza Punti</h2>
                      <p className={t.textSecondary}>Configura quando i punti scadono</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Expiry Type Selection */}
                    <div className={`rounded-xl ${t.card} border p-6`}>
                      <label className={`${t.text} font-medium block mb-4`}>Tipo di Scadenza</label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          onClick={() => handleChange('expiry_type', 'none')}
                          className={`p-4 rounded-xl border transition-all ${
                            settings?.expiry_type === 'none' || (!settings?.expiry_type && !settings?.monthly_reset_enabled)
                              ? 'bg-[#4A5D23]/30 border-[#4A5D23] text-white'
                              : isDark ? 'bg-white/5 border-white/10 text-white/60 hover:border-white/30' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">♾️</div>
                          <p className="font-medium text-sm">Mai</p>
                          <p className={`text-xs ${t.textMuted} mt-1`}>I punti non scadono</p>
                        </button>
                        <button
                          onClick={() => handleChange('expiry_type', 'monthly')}
                          className={`p-4 rounded-xl border transition-all ${
                            settings?.expiry_type === 'monthly' || (!settings?.expiry_type && settings?.monthly_reset_enabled !== false)
                              ? 'bg-[#FF9800]/30 border-[#FF9800] text-white'
                              : isDark ? 'bg-white/5 border-white/10 text-white/60 hover:border-white/30' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">📅</div>
                          <p className="font-medium text-sm">Mensile</p>
                          <p className={`text-xs ${t.textMuted} mt-1`}>Fine di ogni mese</p>
                        </button>
                        <button
                          onClick={() => handleChange('expiry_type', 'specific_date')}
                          className={`p-4 rounded-xl border transition-all ${
                            settings?.expiry_type === 'specific_date'
                              ? 'bg-[#5D1822]/30 border-[#5D1822] text-white'
                              : isDark ? 'bg-white/5 border-white/10 text-white/60 hover:border-white/30' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl mb-2">🎯</div>
                          <p className="font-medium text-sm">Data Specifica</p>
                          <p className={`text-xs ${t.textMuted} mt-1`}>Scelgo io quando</p>
                        </button>
                      </div>
                    </div>

                    {/* Specific Date Picker - shown only when specific_date is selected */}
                    {settings?.expiry_type === 'specific_date' && (
                      <div className="rounded-xl bg-[#5D1822]/20 border border-[#5D1822]/30 p-6 animate-fade-in">
                        <label className={`${t.text} font-medium block mb-4`}>Data di Scadenza Punti</label>
                        <input
                          type="date"
                          value={settings?.expiry_specific_date || ''}
                          onChange={(e) => handleChange('expiry_specific_date', e.target.value)}
                          className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#5D1822]/50`}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <p className={`${t.textMuted} text-sm mt-3`}>
                          I punti di tutti i clienti verranno azzerati il <span className="text-[#C5A059] font-medium">
                            {settings?.expiry_specific_date 
                              ? new Date(settings.expiry_specific_date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                              : 'data non impostata'}
                          </span>
                        </p>
                        
                        {/* Quick Date Buttons */}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <button
                            onClick={() => {
                              const date = new Date();
                              date.setMonth(date.getMonth() + 3);
                              handleChange('expiry_specific_date', date.toISOString().split('T')[0]);
                            }}
                            className={`px-3 py-1.5 ${isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg text-xs transition-colors`}
                          >
                            Tra 3 mesi
                          </button>
                          <button
                            onClick={() => {
                              const date = new Date();
                              date.setMonth(date.getMonth() + 6);
                              handleChange('expiry_specific_date', date.toISOString().split('T')[0]);
                            }}
                            className={`px-3 py-1.5 ${isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg text-xs transition-colors`}
                          >
                            Tra 6 mesi
                          </button>
                          <button
                            onClick={() => {
                              const date = new Date();
                              date.setFullYear(date.getFullYear() + 1);
                              handleChange('expiry_specific_date', date.toISOString().split('T')[0]);
                            }}
                            className={`px-3 py-1.5 ${isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg text-xs transition-colors`}
                          >
                            Tra 1 anno
                          </button>
                          <button
                            onClick={() => {
                              const date = new Date(new Date().getFullYear(), 11, 31);
                              handleChange('expiry_specific_date', date.toISOString().split('T')[0]);
                            }}
                            className={`px-3 py-1.5 ${isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg text-xs transition-colors`}
                          >
                            Fine anno
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Monthly Info */}
                    {(settings?.expiry_type === 'monthly' || (!settings?.expiry_type && settings?.monthly_reset_enabled !== false)) && (
                      <div className="rounded-xl bg-[#FF9800]/20 border border-[#FF9800]/30 p-6 animate-fade-in">
                        <p className="text-[#FF9800] text-sm flex items-start gap-2">
                          <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <span>
                            I punti verranno azzerati automaticamente alla fine di ogni mese (l&apos;ultimo giorno del mese a mezzanotte).
                          </span>
                        </p>
                      </div>
                    )}

                    {/* No Expiry Info */}
                    {settings?.expiry_type === 'none' && (
                      <div className="rounded-xl bg-[#4A5D23]/20 border border-[#4A5D23]/30 p-6 animate-fade-in">
                        <p className="text-[#7CB342] text-sm flex items-start gap-2">
                          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>Punti senza scadenza!</strong> I clienti potranno accumulare punti senza limiti di tempo.
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Expiry Message */}
                    <div className={`rounded-xl ${t.card} border p-6`}>
                      <label className={`${t.text} font-medium block mb-3`}>Messaggio Scadenza (mostrato ai clienti)</label>
                      <textarea
                        value={settings?.expiry_message || 'I punti scadono ogni fine mese'}
                        onChange={(e) => handleChange('expiry_message', e.target.value)}
                        className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#FF9800]/50 resize-none`}
                        rows="2"
                        placeholder="Es: I tuoi punti scadono il 31 dicembre!"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Communications Section */}
            {activeSection === 'communications' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center">
                      <Bell className="w-7 h-7 text-[#C5A059]" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Avvisi ai clienti</h2>
                      <p className={t.textSecondary}>Invia un messaggio nell’app, una notifica push e, quando disponibile, anche via email.</p>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Destinatari</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setAnnouncement({ ...announcement, audience: 'all', user_id: '' })}
                            className={`p-4 rounded-xl border font-semibold flex items-center justify-center gap-2 ${announcement.audience === 'all' ? 'bg-[#C5A059] text-white border-[#C5A059]' : `${t.input}`}`}
                          >
                            <Users size={18} /> Tutti
                          </button>
                          <button
                            type="button"
                            onClick={() => setAnnouncement({ ...announcement, audience: 'single' })}
                            className={`p-4 rounded-xl border font-semibold ${announcement.audience === 'single' ? 'bg-[#5D1822] text-white border-[#5D1822]' : `${t.input}`}`}
                          >
                            Cliente singolo
                          </button>
                        </div>
                      </div>

                      {announcement.audience === 'single' && (
                        <div>
                          <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Cliente</label>
                          <select
                            value={announcement.user_id}
                            onChange={(e) => setAnnouncement({ ...announcement, user_id: e.target.value })}
                            className={`w-full px-4 py-3 rounded-xl border ${t.input}`}
                          >
                            <option value="">Seleziona un cliente</option>
                            {customers.map(customer => (
                              <option key={customer.id} value={customer.id}>
                                {customer.full_name}{customer.phone ? ` - ${customer.phone}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Titolo</label>
                        <input
                          value={announcement.title}
                          onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })}
                          className={`w-full px-4 py-3 rounded-xl border ${t.input}`}
                          maxLength="120"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${t.text}`}>Messaggio</label>
                        <textarea
                          rows="9"
                          value={announcement.message}
                          onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                          className={`w-full px-4 py-3 rounded-xl border ${t.input}`}
                          maxLength="2000"
                        />
                      </div>

                      <label className={`flex items-center gap-3 p-4 rounded-xl border ${t.input}`}>
                        <input
                          type="checkbox"
                          checked={announcement.send_email}
                          onChange={(e) => setAnnouncement({ ...announcement, send_email: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className={t.text}>Invia anche via email ai clienti che hanno un indirizzo valido</span>
                      </label>

                      <button
                        type="button"
                        onClick={handleSendAnnouncement}
                        disabled={sendingAnnouncement}
                        className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white font-bold flex items-center justify-center gap-3 disabled:opacity-50"
                      >
                        <Send size={20} />
                        {sendingAnnouncement ? 'Invio in corso...' : 'Invia comunicazione'}
                      </button>
                    </div>

                    <div>
                      <h3 className={`text-lg font-bold mb-4 ${t.text}`}>Ultimi invii</h3>
                      <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1">
                        {announcementHistory.length === 0 ? (
                          <div className={`p-5 rounded-xl border ${t.input} ${t.textSecondary}`}>Nessuna comunicazione inviata.</div>
                        ) : announcementHistory.map(item => (
                          <div key={item.id} className={`p-4 rounded-xl border ${t.input}`}>
                            <div className="flex justify-between gap-4 mb-2">
                              <p className={`font-bold ${t.text}`}>{item.title}</p>
                              <span className={`text-xs whitespace-nowrap ${t.textMuted}`}>
                                {item.created_at ? new Date(item.created_at).toLocaleString('it-IT') : ''}
                              </span>
                            </div>
                            <p className={`text-sm ${t.textSecondary} line-clamp-3`}>{item.message}</p>
                            <div className={`mt-3 text-xs ${t.textMuted}`}>
                              Destinatari: {item.recipients || 0} · Email: {item.emails_queued || 0} · Push: {item.pushes_queued || 0}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} backdrop-blur-xl border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center">
                      <Shield className="w-7 h-7 text-[#C5A059]" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Sicurezza</h2>
                      <p className={t.textSecondary}>Password, 2FA e backup database</p>
                    </div>
                  </div>
                  <SecuritySettings primaryColor="#5D1822" />
                </div>
              </div>
            )}

            {/* Tools Section */}
            {activeSection === 'tools' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${t.card} backdrop-blur-xl border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center">
                      <Settings className="w-7 h-7 text-[#C5A059]" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Strumenti Avanzati</h2>
                      <p className={t.textSecondary}>Editor del sito e storico transazioni</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Editor Sito */}
                    <Link
                      to="/admin/site-editor"
                      className={`p-6 rounded-xl border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-all group`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <Palette className="w-6 h-6 text-white" />
                        </div>
                        <ChevronRight className={`w-5 h-5 ${t.textMuted} group-hover:translate-x-1 transition-transform`} />
                      </div>
                      <h3 className={`text-lg font-bold ${t.text} mb-2`}>Editor completo del sito</h3>
                      <p className={`text-sm ${t.textSecondary}`}>
                        Gestisci prodotti, categorie, colori, homepage, pagina Ordina, contatti e tutti gli orari
                      </p>
                    </Link>

                    {/* Storico Punti */}
                    <Link
                      to="/admin/points-history"
                      className={`p-6 rounded-xl border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-all group`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                          <History className="w-6 h-6 text-white" />
                        </div>
                        <ChevronRight className={`w-5 h-5 ${t.textMuted} group-hover:translate-x-1 transition-transform`} />
                      </div>
                      <h3 className={`text-lg font-bold ${t.text} mb-2`}>Storico Transazioni</h3>
                      <p className={`text-sm ${t.textSecondary}`}>
                        Vedi tutte le transazioni di punti e sconti dei clienti
                      </p>
                    </Link>
                  </div>
                </div>

                {/* Riavvia App Section */}
                <div className={`rounded-2xl ${t.card} backdrop-blur-xl border p-8`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <RefreshCw className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Riavvia App</h2>
                      <p className={t.textSecondary}>Se qualcosa non funziona, prova a riavviare</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    {/* Ricarica Pagina */}
                    <button
                      onClick={() => {
                        toast.info('Ricaricamento pagina...');
                        setTimeout(() => window.location.reload(), 500);
                      }}
                      className={`p-4 rounded-xl border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-all text-left`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                        <RefreshCw className="w-5 h-5 text-blue-500" />
                      </div>
                      <h3 className={`font-bold ${t.text} mb-1`}>Ricarica</h3>
                      <p className={`text-xs ${t.textSecondary}`}>Aggiorna la pagina</p>
                    </button>

                    {/* Pulisci Cache */}
                    <button
                      onClick={async () => {
                        toast.info('Pulizia cache in corso...');
                        localStorage.removeItem('borgese_cart');
                        sessionStorage.clear();
                        if ('caches' in window) {
                          const names = await caches.keys();
                          await Promise.all(names.map(name => caches.delete(name)));
                        }
                        toast.success('Cache pulita! Ricarico...');
                        setTimeout(() => window.location.reload(), 1000);
                      }}
                      className={`p-4 rounded-xl border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-all text-left`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center mb-3">
                        <Database className="w-5 h-5 text-orange-500" />
                      </div>
                      <h3 className={`font-bold ${t.text} mb-1`}>Pulisci Cache</h3>
                      <p className={`text-xs ${t.textSecondary}`}>Cancella dati temporanei</p>
                    </button>

                    {/* Reset Completo protetto */}
                    <button
                      onClick={() => setShowFullResetModal(true)}
                      className={`p-4 rounded-xl border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-all text-left`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      </div>
                      <h3 className={`font-bold ${t.text} mb-1`}>Reset Completo</h3>
                      <p className={`text-xs ${t.textSecondary}`}>Password obbligatoria, conserva accessi e sito</p>
                    </button>
                  </div>

                  <p className={`mt-4 text-sm ${t.textMuted}`}>
                    💡 Il Reset Completo crea prima un backup di sicurezza, richiede la password e cancella solo i dati operativi.
                  </p>
                </div>
              </div>
            )}

            {/* Danger Zone Section */}
            {activeSection === 'danger' && (
              <div className="space-y-6 animate-fade-in">
                <div className={`rounded-2xl ${isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'} backdrop-blur-xl border p-8`}>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                      <AlertTriangle className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-bold ${t.text}`}>Zona Pericolosa</h2>
                      <p className="text-red-400">Azioni irreversibili - Usa con cautela</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Reset Stats */}
                    <div className={`rounded-xl ${t.card} border p-6 flex items-center justify-between`}>
                      <div>
                        <h3 className={`${t.text} font-semibold mb-1 flex items-center gap-2`}>
                          <Database size={18} />
                          Azzera Statistiche
                        </h3>
                        <p className={`${t.textSecondary} text-sm`}>Resetta tutte le statistiche del sistema</p>
                      </div>
                      <button
                        onClick={() => setShowResetModal(true)}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors font-medium border border-red-500/30"
                      >
                        Azzera
                      </button>
                    </div>

                    {/* Reset All Points */}
                    <div className={`rounded-xl ${t.card} border p-6 flex items-center justify-between`}>
                      <div>
                        <h3 className={`${t.text} font-semibold mb-1 flex items-center gap-2`}>
                          <RefreshCw size={18} />
                          Azzera Punti Clienti
                        </h3>
                        <p className={`${t.textSecondary} text-sm`}>Resetta i punti di TUTTI i clienti a zero</p>
                      </div>
                      <button
                        onClick={() => setShowResetPointsModal(true)}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors font-medium border border-red-500/30"
                      >
                        Azzera Tutti
                      </button>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="mt-6 p-4 rounded-xl bg-red-500/20 border border-red-500/40">
                    <p className="text-red-300 text-sm flex items-start gap-2">
                      <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Attenzione:</strong> ogni azione richiede la password amministratore e crea un backup automatico prima di procedere.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Reset Stats Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8 shadow-2xl`}>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className={`text-2xl font-bold ${t.text} text-center mb-2`}>Azzera Statistiche</h3>
            <p className={`${t.textSecondary} text-center mb-4`}>
              Verranno cancellati lo storico delle transazioni e degli sconti.
              <strong className="text-green-500"> I punti dei clienti resteranno invariati.</strong>
            </p>
            <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'} mb-4`}>
              <p className={`text-xs ${t.textSecondary}`}>
                Prima dell'azzeramento viene creato automaticamente un backup completo sul server.
              </p>
            </div>
            <label className={`block text-sm font-medium ${t.text} mb-2`}>Password amministratore</label>
            <input
              type="password"
              value={statsPassword}
              onChange={(e) => setStatsPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !resettingStats) handleResetStats(); }}
              className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 mb-6`}
              placeholder="Inserisci la password"
              autoComplete="current-password"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowResetModal(false); setStatsPassword(''); }}
                disabled={resettingStats}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                onClick={handleResetStats}
                disabled={resettingStats || !statsPassword}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50"
              >
                {resettingStats ? 'Azzeramento...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Points Modal */}
      {showResetPointsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-md w-full p-8 shadow-2xl`}>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-red-400" />
            </div>
            <h3 className={`text-2xl font-bold ${t.text} text-center mb-2`}>Azzera Punti Clienti</h3>
            <p className={`${t.textSecondary} text-center mb-4`}>
              Stai per azzerare i punti correnti di <strong className="text-red-400">TUTTI</strong> i clienti.
              I punti annullati verranno conservati nello storico.
            </p>
            <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'} mb-4`}>
              <p className={`text-xs ${t.textSecondary}`}>
                Prima dell'azzeramento viene creato automaticamente un backup completo sul server.
              </p>
            </div>
            <label className={`block text-sm font-medium ${t.text} mb-2`}>Password amministratore</label>
            <input
              type="password"
              value={pointsPassword}
              onChange={(e) => setPointsPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !resettingPoints) handleResetMonthlyPoints(); }}
              className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 mb-6`}
              placeholder="Inserisci la password"
              autoComplete="current-password"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowResetPointsModal(false); setPointsPassword(''); }}
                disabled={resettingPoints}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                onClick={handleResetMonthlyPoints}
                disabled={resettingPoints || !pointsPassword}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50"
              >
                {resettingPoints ? 'Azzeramento...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Full Site Reset Modal */}
      {showFullResetModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-lg w-full p-8 shadow-2xl`}>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className={`text-2xl font-bold ${t.text} text-center mb-2`}>Reset Completo Protetto</h3>
            <p className={`${t.textSecondary} text-center mb-5`}>
              Verranno cancellati ordini, statistiche, punti, notifiche e offerte attive.
              Rimarranno account amministratore, clienti, prodotti, logo, colori, grafiche,
              indirizzo, mappa, orari e impostazioni del sito.
            </p>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'} mb-5`}>
              <p className={`text-sm ${t.textSecondary}`}>
                Prima del reset viene scaricato automaticamente un backup completo sul Mac e viene creata anche una copia di sicurezza sul server.
              </p>
            </div>
            <label className={`block text-sm font-medium ${t.text} mb-2`}>Password amministratore</label>
            <input
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !resettingSite) handleFullReset(); }}
              className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 mb-6`}
              placeholder="Inserisci la password"
              autoComplete="current-password"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowFullResetModal(false); setResetPassword(''); }}
                disabled={resettingSite}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                onClick={handleFullReset}
                disabled={resettingSite || !resetPassword}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {resettingSite ? 'Reset in corso...' : 'Conferma Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
